import React, { useEffect, useState, useRef } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTrip } from '../contexts/TripContext';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Plus, CreditCard, DollarSign, Loader2, CheckCircle2, 
  Clock, AlertCircle, TrendingUp, Users, Sparkles, Upload, Receipt, X, History,
  Type, Tag, Calendar, Wallet, FileText, ArrowRight
} from 'lucide-react';
import { format, parseISO, differenceInDays } from 'date-fns';
import { ptBR, enUS } from 'date-fns/locale';
import { toast } from 'sonner';
import { AIReminderButton, GroupReminderButton } from '../components/common/AIReminder';
import { PaymentStatusModal } from '../components/trip/TransparencyModals';
import { PaymentHistoryModal } from '../components/trip/PaymentHistoryModal';
import { ScreenHelp } from '../components/common/ScreenHelp';
import { MobileFormModal, MobileFormFooter } from '../components/common/MobileFormModal';
import { LabelWithTooltip, FIELD_TIPS } from '../components/common/FieldTooltip';
import { PageHeader } from '../components/common/PageHeader';
import PaymentSplitModal from '../components/payments/PaymentSplitModal';
import CurrencyInput from '../components/common/CurrencyInput';

export default function TripPayments() {
  const { tripId } = useParams();
  const context = useOutletContext();
  const { isOrganizer, members, trip } = context || {};
  const { user, api } = useAuth();
  const { t, language } = useLanguage();
  const dateLocale = language === 'en-US' ? enUS : ptBR;
  const { payments, fetchPayments, createPayment, markAsPaid, loading } = useTrip();

  // Category and status configs with translations
  const categoryConfig = {
    accommodation: { label: t('payments.categories.accommodation'), icon: '🏨', color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400' },
    transport: { label: t('payments.categories.transport'), icon: '🚗', color: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' },
    food: { label: t('payments.categories.food'), icon: '🍽️', color: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400' },
    activities: { label: t('payments.categories.activities'), icon: '🎯', color: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400' },
    shopping: { label: t('payments.categories.shopping'), icon: '🛍️', color: 'bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-400' },
    other: { label: t('payments.categories.other'), icon: '📦', color: 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300' },
  };

  const statusConfig = {
    pending: { label: t('payments.status.pending'), color: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400' },
    partial: { label: t('payments.status.partial'), color: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400' },
    completed: { label: t('payments.status.paid'), color: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' },
  };
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [splitDialogOpen, setSplitDialogOpen] = useState(false);
  const [payDialogOpen, setPayDialogOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [payAmount, setPayAmount] = useState('');
  const [proofFile, setProofFile] = useState(null);
  const [uploadingProof, setUploadingProof] = useState(false);
  const [walletBalance, setWalletBalance] = useState(0);
  const [payingWithWallet, setPayingWithWallet] = useState(false);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    total_amount: '',
    category: 'other',
    payment_type: 'common',
    due_date: '',
  });
  
  // Payment split state for two-step flow
  const [paymentSplitData, setPaymentSplitData] = useState([]);
  
  // Detect mobile
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (tripId) {
      fetchPayments(tripId);
      fetchWalletBalance();
    }
  }, [tripId, fetchPayments]);

  // Fetch wallet balance
  const fetchWalletBalance = async () => {
    try {
      const response = await api.get(`/trips/${tripId}/wallets/me`);
      setWalletBalance(response.data?.balance || 0);
    } catch (error) {
      setWalletBalance(0);
    }
  };

  // Pay with wallet
  const handlePayWithWallet = async (payment) => {
    const myPayment = payment.member_payments?.find(mp => mp.user_id === user?.id);
    if (!myPayment) return;

    const amountRemaining = myPayment.amount_due - (myPayment.amount_paid || 0);
    if (walletBalance <= 0) {
      toast.error('Você não tem saldo disponível na carteira');
      return;
    }

    setPayingWithWallet(true);
    try {
      const response = await api.post(`/trips/${tripId}/payments/${payment.id}/pay-with-wallet`);
      toast.success(response.data.message);
      fetchPayments(tripId);
      fetchWalletBalance();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao pagar com saldo');
    } finally {
      setPayingWithWallet(false);
    }
  };

  // Step 1: Save payment details and open split modal
  const handleSubmitDetails = async (e) => {
    e?.preventDefault();
    
    if (!formData.title || !formData.total_amount) {
      toast.error('Preencha os campos obrigatórios');
      return;
    }

    // Initialize payment split data with all members
    const initialSplitData = members?.map(m => ({
      user_id: m.user_id,
      user_name: m.user_name || m.name || 'Membro',
      quotas: 0,
      manualAmount: 0,
    })) || [];
    
    setPaymentSplitData(initialSplitData);
    setDialogOpen(false);
    setSplitDialogOpen(true);
  };

  // Step 2: Create payment with split data
  const handleCreatePaymentWithSplit = async () => {
    const totalAmount = parseFloat(formData.total_amount);
    const totalParticipants = paymentSplitData.length;
    
    // Check if using manual mode (any manualAmount > 0)
    const isManualMode = paymentSplitData.some(p => p.manualAmount > 0);
    
    if (isManualMode) {
      // Manual mode validation
      const participantsWithAmount = paymentSplitData.filter(p => p.manualAmount > 0);
      if (participantsWithAmount.length === 0) {
        toast.error('Distribua o valor entre pelo menos um participante');
        return;
      }
      
      const totalManualAssigned = paymentSplitData.reduce((sum, p) => sum + (p.manualAmount || 0), 0);
      const remainingAmount = totalAmount - totalManualAssigned;
      
      if (Math.abs(remainingAmount) > 0.01) {
        if (remainingAmount > 0) {
          toast.error(`Faltam R$ ${remainingAmount.toFixed(2)} para completar o rateio.`);
        } else {
          toast.error(`Excesso de R$ ${Math.abs(remainingAmount).toFixed(2)} no rateio.`);
        }
        return;
      }
      
      try {
        const selectedMemberIds = participantsWithAmount.map(p => p.user_id);
        await createPayment(tripId, {
          title: formData.title,
          description: formData.description || null,
          total_amount: totalManualAssigned,
          category: formData.category,
          payment_type: 'selective',
          due_date: formData.due_date ? new Date(formData.due_date).toISOString() : null,
          selected_member_ids: selectedMemberIds,
          manual_amounts: participantsWithAmount.map(p => ({
            user_id: p.user_id,
            amount: p.manualAmount
          })),
        });
        toast.success('Pagamento criado com sucesso!');
        resetFormAndClose();
        fetchPayments(tripId);
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Erro ao criar pagamento');
      }
    } else {
      // Quota mode validation
      const participantsWithQuotas = paymentSplitData.filter(p => p.quotas > 0);
      if (participantsWithQuotas.length === 0) {
        toast.error('Distribua pelo menos uma cota');
        return;
      }
      
      const totalQuotas = paymentSplitData.reduce((sum, p) => sum + p.quotas, 0);
      const quotaValue = totalParticipants > 0 ? totalAmount / totalParticipants : 0;
      
      // Check if total quotas distributed matches number of participants
      if (totalQuotas !== totalParticipants) {
        const diff = totalParticipants - totalQuotas;
        if (diff > 0) {
          toast.error(`Faltam ${diff} cota(s) para completar o rateio.`);
        } else {
          toast.error(`Excesso de ${Math.abs(diff)} cota(s) no rateio.`);
        }
        return;
      }
      
      try {
        const selectedMemberIds = participantsWithQuotas.map(p => p.user_id);
        const actualTotalAmount = totalQuotas * quotaValue;
        
        await createPayment(tripId, {
          title: formData.title,
          description: formData.description || null,
          total_amount: actualTotalAmount,
          category: formData.category,
          payment_type: 'selective',
          due_date: formData.due_date ? new Date(formData.due_date).toISOString() : null,
          selected_member_ids: selectedMemberIds,
        });
        toast.success('Pagamento criado com sucesso!');
        resetFormAndClose();
        fetchPayments(tripId);
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Erro ao criar pagamento');
      }
    }
  };
  
  // Helper to reset form and close all modals
  const resetFormAndClose = () => {
    setDialogOpen(false);
    setSplitDialogOpen(false);
    setFormData({
      title: '',
      description: '',
      total_amount: '',
      category: 'other',
      payment_type: 'common',
      due_date: '',
    });
    setPaymentSplitData([]);
  };
  
  // Cancel split and go back to details
  const handleCancelSplit = () => {
    setSplitDialogOpen(false);
    setDialogOpen(true);
  };

  // Format currency helper
  const formatCurrency = (value) => {
    if (!value && value !== 0) return '';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
    }).format(value);
  };

  const uploadProofFile = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('description', `Comprovante - ${selectedPayment?.title || 'Pagamento'}`);
    
    const response = await api.post(`/trips/${tripId}/files`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    
    return response.data.id;
  };

  const handlePay = async () => {
    if (!selectedPayment || !payAmount) return;

    try {
      setUploadingProof(true);
      
      let proofFileId = null;
      
      // Upload proof if provided
      if (proofFile) {
        proofFileId = await uploadProofFile(proofFile);
      }
      
      // Mark as paid with optional proof
      await api.post(`/trips/${tripId}/payments/${selectedPayment.id}/pay`, null, {
        params: {
          amount: parseFloat(payAmount),
          proof_url: proofFileId
        }
      });
      
      toast.success('Pagamento registrado!');
      setPayDialogOpen(false);
      setSelectedPayment(null);
      setPayAmount('');
      setProofFile(null);
      fetchPayments(tripId);
    } catch (error) {
      toast.error('Erro ao registrar pagamento');
    } finally {
      setUploadingProof(false);
    }
  };

  const openPayDialog = (payment) => {
    const myPayment = payment.member_payments?.find(mp => mp.user_id === user?.id);
    setSelectedPayment(payment);
    setPayAmount(myPayment ? (myPayment.amount_due - myPayment.amount_paid).toString() : '');
    setProofFile(null);
    setPayDialogOpen(true);
  };

  const handleProofFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type (images only for proofs)
      if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
        toast.error('Envie uma imagem ou PDF como comprovante');
        return;
      }
      // Validate size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Arquivo muito grande (máx. 10MB)');
        return;
      }
      setProofFile(file);
    }
  };

  // Calculate totals
  const totalAmount = payments.reduce((acc, p) => acc + p.total_amount, 0);
  const totalPaid = payments.reduce((acc, p) => acc + (p.total_paid || 0), 0);
  const totalPending = totalAmount - totalPaid;
  const progressPercent = totalAmount > 0 ? (totalPaid / totalAmount) * 100 : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title="Pagamentos"
        subtitle="Gerencie e divida as despesas da viagem"
        helpScreen="payments"
        actions={
          <div className="flex gap-2">
            {isOrganizer && totalPending > 0 && (
              <GroupReminderButton tripId={tripId} />
            )}
            {isOrganizer && (
              <>
                {/* Mobile Modal */}
                {isMobile ? (
                  <Button 
                    className="rounded-full bg-orange-500 hover:bg-orange-600"
                    onClick={() => setDialogOpen(true)}
                    data-testid="add-payment-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Novo
                  </Button>
                ) : (
                  /* Desktop Modal */
                  <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogTrigger asChild>
                      <Button 
                        className="rounded-full bg-orange-500 hover:bg-orange-600"
                        data-testid="add-payment-btn"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Novo pagamento
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle>Novo Pagamento</DialogTitle>
                        <DialogDescription>
                          Passo 1: Detalhes do pagamento
                        </DialogDescription>
                      </DialogHeader>
                      <form onSubmit={handleSubmitDetails} className="space-y-4 mt-4">
                        <div>
                          <Label htmlFor="title">Título *</Label>
                          <div className="relative mt-1">
                            <Type className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <Input
                              id="title"
                              value={formData.title}
                              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                              placeholder="Ex: Hospedagem Airbnb"
                              className="pl-10"
                              data-testid="payment-title-desktop"
                            />
                          </div>
                        </div>
                        
                        <div>
                          <Label htmlFor="total_amount">Valor total *</Label>
                          <div className="relative mt-1">
                            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <Input
                              id="total_amount"
                              type="number"
                              value={formData.total_amount}
                              onChange={(e) => setFormData({ ...formData, total_amount: e.target.value })}
                              placeholder="0,00"
                              className="pl-10"
                              data-testid="payment-amount-desktop"
                            />
                          </div>
                        </div>
                        
                        <div>
                          <Label>Categoria</Label>
                          <Select
                            value={formData.category}
                            onValueChange={(value) => setFormData({ ...formData, category: value })}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(categoryConfig).map(([key, config]) => (
                                <SelectItem key={key} value={key}>
                                  {config.icon} {config.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div>
                          <Label htmlFor="due_date">Vencimento (opcional)</Label>
                          <div className="relative mt-1">
                            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <Input
                              id="due_date"
                              type="date"
                              value={formData.due_date}
                              onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                              className="pl-10"
                            />
                          </div>
                        </div>
                        
                        <div>
                          <Label htmlFor="description">Descrição (opcional)</Label>
                          <Textarea
                            id="description"
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            placeholder="Detalhes do pagamento..."
                            rows={2}
                            className="mt-1"
                          />
                        </div>
                        
                        <div className="flex gap-3 pt-2">
                          <Button
                            type="button"
                            variant="outline"
                            className="flex-1 rounded-full"
                            onClick={() => setDialogOpen(false)}
                          >
                            Cancelar
                          </Button>
                          <Button
                            type="submit"
                            className="flex-1 rounded-full bg-orange-500 hover:bg-orange-600"
                          >
                            Próximo
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                )}
              </>
            )}
          </div>
        }
      />

      {/* Mobile Form Modal - Step 1: Payment Details */}
      {isMobile && isOrganizer && (
        <MobileFormModal
                    isOpen={dialogOpen}
                    onClose={() => setDialogOpen(false)}
                    title="Novo pagamento"
                    footer={
                      <MobileFormFooter
                        onCancel={() => setDialogOpen(false)}
                        onSubmit={handleSubmitDetails}
                        cancelText="Cancelar"
                        submitText={
                          <span className="flex items-center gap-2">
                            Próximo
                            <ArrowRight className="w-4 h-4" />
                          </span>
                        }
                      />
                    }
                  >
                    <form onSubmit={handleSubmitDetails} className="space-y-2">
                      <div className="py-2">
                        <label className="text-xs text-slate-500 dark:text-slate-400 block mb-1">Título *</label>
                        <div className="flex items-center gap-3 border-b border-slate-200 dark:border-slate-700 focus-within:border-orange-500">
                          <Type className="w-5 h-5 text-slate-400 flex-shrink-0" />
                          <input
                            value={formData.title}
                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                            placeholder="Ex: Hospedagem Airbnb"
                            className="w-full bg-transparent border-0 focus:ring-0 px-0 py-3 text-base placeholder:text-slate-400 text-slate-900 dark:text-white outline-none"
                            data-testid="payment-title-mobile"
                          />
                        </div>
                      </div>
                      
                      <div className="py-2">
                        <label className="text-xs text-slate-500 dark:text-slate-400 block mb-1">Valor total *</label>
                        <div className="flex items-center gap-3 border-b border-slate-200 dark:border-slate-700 focus-within:border-orange-500">
                          <DollarSign className="w-5 h-5 text-slate-400 flex-shrink-0" />
                          <input
                            type="number"
                            value={formData.total_amount}
                            onChange={(e) => setFormData({ ...formData, total_amount: e.target.value })}
                            placeholder="0,00"
                            className="w-full bg-transparent border-0 focus:ring-0 px-0 py-3 text-base placeholder:text-slate-400 text-slate-900 dark:text-white outline-none"
                            data-testid="payment-amount-mobile"
                          />
                        </div>
                      </div>
                      
                      <div className="py-2">
                        <label className="text-xs text-slate-500 dark:text-slate-400 block mb-1">Categoria</label>
                        <div className="flex items-center gap-3 border-b border-slate-200 dark:border-slate-700 focus-within:border-orange-500">
                          <Tag className="w-5 h-5 text-slate-400 flex-shrink-0" />
                          <Select
                            value={formData.category}
                            onValueChange={(value) => setFormData({ ...formData, category: value })}
                          >
                            <SelectTrigger className="w-full bg-transparent border-0 focus:ring-0 px-0 py-3 text-base h-auto rounded-none shadow-none">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(categoryConfig).map(([key, config]) => (
                                <SelectItem key={key} value={key}>
                                  {config.icon} {config.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      <div className="py-2">
                        <label className="text-xs text-slate-500 dark:text-slate-400 block mb-1">Vencimento (opcional)</label>
                        <div className="flex items-center gap-3 border-b border-slate-200 dark:border-slate-700 focus-within:border-orange-500">
                          <Calendar className="w-5 h-5 text-slate-400 flex-shrink-0" />
                          <input
                            type="date"
                            value={formData.due_date}
                            onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                            className="w-full bg-transparent border-0 focus:ring-0 px-0 py-3 text-base placeholder:text-slate-400 text-slate-900 dark:text-white outline-none"
                          />
                        </div>
                      </div>
                      
                      <div className="py-2">
                        <label className="text-xs text-slate-500 dark:text-slate-400 block mb-1">Descrição (opcional)</label>
                        <div className="flex items-start gap-3 border-b border-slate-200 dark:border-slate-700 focus-within:border-orange-500">
                          <FileText className="w-5 h-5 text-slate-400 flex-shrink-0 mt-3" />
                          <textarea
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            placeholder="Detalhes do pagamento..."
                            rows={2}
                            className="w-full bg-transparent border-0 focus:ring-0 px-0 py-3 text-base placeholder:text-slate-400 text-slate-900 dark:text-white outline-none resize-none"
                          />
                        </div>
                      </div>
                    </form>
                </MobileFormModal>
      )}


      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-slate-100 dark:border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-slate-600 dark:text-slate-400" />
              </div>
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">Total</p>
                <p className="font-bold text-lg text-slate-900 dark:text-white">
                  R$ {totalAmount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-100 dark:border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">Pago</p>
                <p className="font-bold text-lg text-green-600">
                  R$ {totalPaid.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-100 dark:border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-yellow-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">Pendente</p>
                <p className="font-bold text-lg text-yellow-600">
                  R$ {totalPending.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Progress */}
      <Card className="border-slate-100 dark:border-slate-700">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Progresso dos pagamentos</span>
            <span className="text-sm text-slate-500 dark:text-slate-400">{progressPercent.toFixed(0)}%</span>
          </div>
          <Progress value={progressPercent} className="h-2" />
        </CardContent>
      </Card>

      {/* Payments List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
        </div>
      ) : payments.length === 0 ? (
        <Card className="border-slate-100 dark:border-slate-700 border-dashed">
          <CardContent className="py-12 text-center">
            <CreditCard className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="font-heading font-bold text-lg text-slate-900 dark:text-white mb-2">
              Nenhum pagamento
            </h3>
            <p className="text-slate-500 dark:text-slate-400">
              Adicione pagamentos para dividir as despesas
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {payments.map((payment, index) => {
            const category = categoryConfig[payment.category] || categoryConfig.other;
            const status = statusConfig[payment.status] || statusConfig.pending;
            const myPayment = payment.member_payments?.find(mp => mp.user_id === user?.id);
            const amountPerPerson = payment.total_amount / (payment.member_payments?.length || 1);
            const myAmountDue = myPayment?.amount_due || amountPerPerson;
            const myAmountPaid = myPayment?.amount_paid || 0;
            const myIsPaid = myPayment?.is_paid || false;

            return (
              <motion.div
                key={payment.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card className="border-slate-100 dark:border-slate-700 hover:shadow-card-hover transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-xl ${category.color} flex items-center justify-center text-lg`}>
                          {category.icon}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-slate-900 dark:text-white">{payment.title}</h3>
                            <Badge className={status.color}>{status.label}</Badge>
                          </div>
                          {payment.description && (
                            <p className="text-sm text-slate-500 mt-1">{payment.description}</p>
                          )}
                          <p className="text-sm text-slate-500 mt-1">
                            {payment.member_payments?.length || 0} participantes
                          </p>
                          {payment.itinerary_item_id && (
                            <p className="text-xs text-orange-500 mt-1 flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              Roteiro: {payment.itinerary_item_title || 'Atividade vinculada'}
                            </p>
                          )}
                          {payment.due_date && (
                            <p className="text-xs text-slate-400 mt-1">
                              Vencimento: {format(parseISO(payment.due_date), "dd/MM/yyyy", { locale: ptBR })}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-lg text-slate-900 dark:text-white">
                          R$ {payment.total_amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </p>
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                          Sua parte: R$ {myAmountDue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </p>
                      </div>
                    </div>

                    {/* My Payment Status */}
                    <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-700">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {myIsPaid ? (
                            <CheckCircle2 className="w-5 h-5 text-green-500" />
                          ) : (
                            <AlertCircle className="w-5 h-5 text-yellow-500" />
                          )}
                          <span className="text-sm text-slate-600 dark:text-slate-400">
                            {myIsPaid 
                              ? 'Você pagou sua parte' 
                              : `Falta pagar: R$ ${(myAmountDue - myAmountPaid).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
                            }
                          </span>
                        </div>
                        <div className="flex gap-2">
                          {/* View payment status button */}
                          <PaymentStatusModal
                            tripId={tripId}
                            paymentId={payment.id}
                            paymentTitle={payment.title}
                            totalAmount={payment.total_amount}
                            isOrganizer={isOrganizer}
                            trigger={
                              <Button
                                variant="outline"
                                size="sm"
                                className="rounded-full"
                                data-testid={`view-status-${payment.id}`}
                              >
                                <Users className="w-4 h-4 mr-1" />
                                {payment.paid_count}/{payment.total_members}
                              </Button>
                            }
                          />
                          {!myIsPaid && walletBalance > 0 && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="rounded-full border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white"
                              onClick={() => handlePayWithWallet(payment)}
                              disabled={payingWithWallet}
                              data-testid={`pay-wallet-btn-${payment.id}`}
                            >
                              {payingWithWallet ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <>
                                  <Wallet className="w-4 h-4 mr-1" />
                                  Saldo
                                </>
                              )}
                            </Button>
                          )}
                          {!myIsPaid && (
                            <Button
                              size="sm"
                              className="rounded-full bg-green-500 hover:bg-green-600"
                              onClick={() => openPayDialog(payment)}
                              data-testid={`pay-btn-${payment.id}`}
                            >
                              Pagar
                            </Button>
                          )}
                        </div>
                      </div>
                      
                      {/* AI Reminder for organizer - show pending members */}
                      {isOrganizer && payment.member_payments?.some(mp => !mp.is_paid) && (
                        <div className="mt-3 pt-3 border-t border-slate-50">
                          <p className="text-xs text-slate-500 mb-2">Membros pendentes:</p>
                          <div className="flex flex-wrap gap-2">
                            {payment.member_payments
                              .filter(mp => !mp.is_paid)
                              .map(mp => {
                                const member = members?.find(m => m.user_id === mp.user_id);
                                const daysOverdue = payment.due_date 
                                  ? Math.max(0, differenceInDays(new Date(), parseISO(payment.due_date)))
                                  : 0;
                                return member ? (
                                  <AIReminderButton
                                    key={mp.user_id}
                                    memberName={member.name || 'Membro'}
                                    amount={mp.amount_due - (mp.amount_paid || 0)}
                                    dueDate={payment.due_date ? format(parseISO(payment.due_date), "dd/MM/yyyy", { locale: ptBR }) : 'Não definida'}
                                    tripName={trip?.name || 'Viagem'}
                                    daysOverdue={daysOverdue}
                                  />
                                ) : null;
                              })}
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Pay Dialog */}
      <Dialog open={payDialogOpen} onOpenChange={setPayDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Registrar pagamento</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-slate-600 dark:text-slate-400">
              {selectedPayment?.title}
            </p>
            <div>
              <Label htmlFor="pay_amount">Valor a pagar (R$)</Label>
              <div className="relative mt-1">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="pay_amount"
                  type="number"
                  value={payAmount}
                  onChange={(e) => setPayAmount(e.target.value)}
                  placeholder="0,00"
                  className="pl-10"
                />
              </div>
            </div>
            
            {/* Proof Upload */}
            <div>
              <Label>Comprovante (opcional)</Label>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleProofFileSelect}
                accept="image/*,.pdf"
                className="hidden"
              />
              
              {proofFile ? (
                <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Receipt className="w-4 h-4 text-green-600" />
                    <span className="text-sm text-green-700 truncate max-w-[200px]">
                      {proofFile.name}
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={() => setProofFile(null)}
                  >
                    <X className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                  </Button>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full mt-2"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Anexar comprovante
                </Button>
              )}
              <p className="text-xs text-slate-500 mt-1">
                Imagem ou PDF (máx. 10MB)
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1 rounded-full"
                onClick={() => setPayDialogOpen(false)}
                disabled={uploadingProof}
              >
                Cancelar
              </Button>
              <Button
                className="flex-1 rounded-full bg-green-500 hover:bg-green-600"
                onClick={handlePay}
                disabled={uploadingProof}
              >
                {uploadingProof ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  'Confirmar'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View my payment history button - shown at bottom */}
      <div className="mt-6 pt-4 border-t border-slate-100 dark:border-slate-700">
        <PaymentHistoryModal
          tripId={tripId}
          memberId={user?.id}
          memberName={user?.name || 'Eu'}
          memberAvatar={user?.avatar_emoji}
          isOrganizer={isOrganizer}
          trigger={
            <Button 
              variant="outline" 
              className="w-full rounded-full"
              data-testid="my-payment-history-btn"
            >
              <History className="w-4 h-4 mr-2" />
              Meu histórico de pagamentos
            </Button>
          }
        />
      </div>

      {/* Payment Split Modal - Step 2 */}
      <PaymentSplitModal
        open={splitDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setSplitDialogOpen(false);
          }
        }}
        paymentTitle={formData.title}
        totalAmount={parseFloat(formData.total_amount) || 0}
        members={members}
        paymentSplitData={paymentSplitData}
        setPaymentSplitData={setPaymentSplitData}
        formatCurrency={formatCurrency}
        onSave={handleCreatePaymentWithSplit}
        onCancel={handleCancelSplit}
      />
    </div>
  );
}
